import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Phone, ArrowRight, BookOpen } from "lucide-react";
import { BRAND } from "@/config/brand";
import PlugitAIChat from "@/pages/PlugitAIChat";
import MobilePocketPopup from "@/pages/MobilePocketPopup";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";

const SORO_SCRIPT_SRC =
  "https://app.trysoro.com/api/embed/83880c57-7ca2-4e96-9b6c-92600243f0fe";
const SORO_SCRIPT_ID = "soro-embed-script";

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}

export default function Blog() {
  // Inject the Soro embed script once. If we've already appended it (e.g. after
  // client-side nav back to this page), the third-party script itself is
  // responsible for re-rendering into #soro-blog. We keep the script tag around
  // so navigation stays snappy.
  useEffect(() => {
    const origin =
      process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    document.title =
      "The PlugitAI Blog — SEO, AI Sales Bots & Small-Business Web Design";
    upsertMeta(
      "description",
      "Kit and the PlugitAI team break down what actually works in small-business web design, local SEO, and AI-powered sales agents. No fluff — just what we do for real clients across DFW."
    );
    upsertMeta("robots", "index,follow,max-image-preview:large");
    upsertCanonical(`${origin}/blog`);

    // Only append the script once per session
    if (!document.getElementById(SORO_SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SORO_SCRIPT_ID;
      s.src = SORO_SCRIPT_SRC;
      s.defer = true;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <div
      className="min-h-screen w-full pb-24 md:pb-0"
      style={{
        background: C.ink,
        color: C.text,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
      data-testid="blog-page"
    >
      <TopNav />

      {/* Ambient gradient bg */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `radial-gradient(1200px 700px at 15% -10%, ${C.blueBright}22, transparent 60%),
                       radial-gradient(900px 600px at 90% 15%, ${teal}18, transparent 60%),
                       radial-gradient(1400px 900px at 60% 110%, ${gold}0e, transparent 70%)`,
        }}
      />

      {/* Hero */}
      <section className="relative z-10 pt-28 md:pt-40 pb-10 md:pb-14">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <nav
            className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-8"
            style={{ color: C.textFade }}
          >
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>Blog</span>
          </nav>

          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.24em] mb-6"
            style={{
              background: `${teal}18`,
              color: teal,
              border: `1px solid ${teal}66`,
            }}
          >
            <BookOpen size={11} />
            The PlugitAI blog
          </div>

          <h1
            className="font-black tracking-[-0.02em] leading-[0.98]"
            style={{ fontSize: "clamp(2.4rem, 5.4vw, 5rem)", color: "white" }}
            data-testid="blog-h1"
          >
            What actually works —{" "}
            <span
              style={{
                backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              from our own client builds
            </span>
            .
          </h1>

          <p
            className="mt-6 max-w-[720px] text-base md:text-lg leading-relaxed"
            style={{ color: C.textDim }}
          >
            Kit and the team break down the real levers behind ranking a
            small-business site in a hyper-local market — SEO, AI sales bots,
            page architecture, and the boring plumbing most agencies never
            mention. No fluff. Just what we do for actual clients.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="tel:+12148155966"
              className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-semibold text-sm"
              style={{
                background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
                color: "white",
                boxShadow: `0 18px 40px -18px ${teal}aa`,
              }}
              data-testid="blog-cta-call"
            >
              <Phone size={14} /> (214) 815-5966 <ArrowRight size={14} />
            </a>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-semibold text-sm border"
              style={{ borderColor: `${teal}66`, color: "white" }}
            >
              Meet the team of 4
            </Link>
          </div>
        </div>
      </section>

      {/* Soro embed container */}
      <section className="relative z-10 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <div
            className="rounded-3xl border p-4 md:p-8 min-h-[400px]"
            style={{
              borderColor: C.line,
              background: "rgba(11,21,36,0.72)",
              backdropFilter: "blur(10px)",
              boxShadow: `0 40px 120px -60px ${teal}44`,
            }}
          >
            {/* Third-party Soro embed mounts into this div */}
            <div id="soro-blog" data-testid="soro-blog-mount" />

            {/* Fallback message while the script initializes / if it errors */}
            <noscript>
              <p style={{ color: C.textDim }}>
                Please enable JavaScript to view the blog, or{" "}
                <a
                  href="tel:+12148155966"
                  style={{ color: teal, textDecoration: "underline" }}
                >
                  call Kit at (214) 815-5966
                </a>
                .
              </p>
            </noscript>
          </div>
        </div>
      </section>

      <PlugitAIChat />
      <MobilePocketPopup />
    </div>
  );
}

function TopNav() {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: "rgba(10,15,30,0.72)",
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src={BRAND.logos.plugitai}
            alt="PlugitAI"
            className="h-10 md:h-12 w-auto"
          />
          <span
            className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark"
            style={{ borderColor: C.line }}
          >
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a
          href="tel:+12148155966"
          className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
          style={{
            background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
            color: "white",
          }}
          data-testid="blog-nav-cta"
        >
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
